import styled from 'styled-components'
import PropTypes from 'prop-types'
import { t } from 'ttag'
import { useMutation } from 'react-query'
import { useContext, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'

import { Globals } from '@h2/globals'
import { api, errors } from '@h2/api'
import { Card } from '@h2/common/components/card'
import { userJoinedSite } from '@h2/common/actions/userJoinedSite'
import { features } from '@h2/components/media'
import { PG, SLB, NAVIANCEBLUE } from '@h2/components/colors'
import { Text } from '@h2/components/typography/text'
import { Field } from '@h2/components/inputs/field'
import { TextInput } from '@h2/components/inputs/text'
import { Alert } from '@h2/components/display/alert'
import { useForm } from '@h2/common/components/hooks/useForm'
import { Loader } from '@h2/components/loading/loader'
import { startSession } from '@h2/features/auth'
import { Checkbox } from '@h2/components/inputs/checkbox'
import { Link } from '@h2/common/components/link'

import { Create } from '../highlights/create'
import { Submit } from '../submit'
import { Controls } from '../controls'
import { useNavigator } from '../../hooks/useNavigator'

const Form = styled.form`
  display: flex;
  flex-direction: column;
  height: 100%;

  > h2 {
    font-size: 24px;
    line-height: 28px;
  }

  > fieldset {
    flex-direction: column;
  }

  @media (${features.minWidth.laptop}) {
    > h2 {
      font-size: 30px;
      line-height: 34px;
    }

    > fieldset {
      flex-direction: row;
    }
  }
`
const CheckboxWrap = styled.div`
  display: flex;
  align-items: flex-start;
`
const WrapText = styled(Text)`
  margin-left: 8px;
`
const Membership = ({ inDialog, properties, renderPanel }) => {
  const dispatch = useDispatch()
  const { environment, setViewMode } = useContext(Globals)
  const navigator = useNavigator()
  const code = useMemo(() => properties.authorizationCode, [properties.authorizationCode])
  const [errorMsg, setErrorMsg] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const { externalIdProperties, isNavianceUser } = useContext(Globals)
  const isExternalIdRequired = useMemo(
    () => externalIdProperties?.required && !properties.externalId,
    [externalIdProperties, properties.externalId]
  )
  const [isChecked, setIsChecked] = useState(false)
  const handleCheckboxChange = () => {
    setIsChecked(!isChecked)
  }
  const { register, error, handleOnSubmit } = useForm({
    externalId(form) {
      if (isExternalIdRequired && !form.externalId?.trim()) {
        return t`Library Card is required`
      }
    }
  })
  const { mutate: joinSite, isLoading: isJoining } = useMutation(
    form =>
      api.fetch('/auth/join/respond', {
        method: 'POST',
        body: JSON.stringify({
          code,
          externalId: form.externalId,
          kind: 'ApprovalBySelf',
          accepted: isChecked
        })
      }),
    {
      // Triggered right before the mutation function is called
      onMutate: () => setIsLoading(true),

      onSuccess: ({ user, membership }) => {
        setIsLoading(false)

        const { removed, accepted, userCanAccept, authorizedPersonEmail } = membership

        if (removed) {
          return
        }

        // If membership is accepted or the user is allowed to accept it, handle successful join flow
        if (accepted || userCanAccept) {
          dispatch(userJoinedSite(inDialog))
          dispatch(startSession({ user, environment, setViewMode }))

          if (properties.isNavianceUser) {
            return navigator('/naviancelinkaccount/register')
          }

          // Otherwise, show the 'complete' panel
          return renderPanel('complete')
        }

        // If the user cannot accept the membership
        if (userCanAccept === false) {
          // If email is not sent to parent, show the 'parentApproval' panel
          return !authorizedPersonEmail
            ? renderPanel('parentApproval')
            : renderPanel('pending', { authorizedPersonEmail }) //show the 'pending' panel when email is sent to parent
        }

        // Fallback to showing the 'membership' panel if no conditions are met
        renderPanel('membership')
      },

      onError: err => {
        setIsLoading(false)
        setErrorMsg(errors.getMessage(err))
      }
    }
  )
  const isSubmitDisabled = useMemo(() => {
    return isJoining || !isChecked
  }, [isJoining, isChecked])

  if (!isExternalIdRequired && isLoading) {
    return <Loader />
  }

  return (
    <Card inDialog={inDialog}>
      <Card.Highlight
        color={isNavianceUser ? NAVIANCEBLUE : properties.isNavigator ? SLB : PG}>
        <Create
          type={
            isNavianceUser
              ? 'navianceUser'
              : properties.isNavigator
              ? 'navigator'
              : 'explorer'
          }
        />
      </Card.Highlight>
      <Card.Content>
        <Form
          id="reg-membership"
          onSubmit={handleOnSubmit(form => joinSite(form))}
          noValidate>
          <Alert
            margin="0 0 15px 0"
            type="error"
            isVisible={Boolean(errorMsg)}
            message={errorMsg}
            onClose={() => setErrorMsg('')}
          />

          {isExternalIdRequired && (
            <>
              <Text size="medium" margin="0">
                {t`Please enter a Library Card to associate with your account.`}
              </Text>
              <Field
                isRequired
                width="100%"
                margin="15px 0 15px 0"
                label={t`Library Card`}
                desc={error.externalId}
                status={error.externalId ? 'error' : 'default'}>
                <TextInput size="small" {...register('externalId', { required: true })} />
              </Field>
            </>
          )}
          <CheckboxWrap>
            <Checkbox isChecked={isChecked} onChange={handleCheckboxChange} />
            <WrapText>
              {t`BY CLICKING THE “*I ACCEPT*” BUTTON, YOU AGREE TO: 
                Grant PowerSchool consent to collect, display, and process your Personal data to provide you the service; and
                Accept the terms of the Terms of Use (“ToU”) for Headed2 and will use the site accordingly.
              `}
              <Link ssr={true} color={PG} href="/privacy">
                {' '}
                {t`Headed 2 Privacy Supplement`}
              </Link>
              , {t`and`}
              <Link color={PG} href="https://www.powerschool.com/privacy/">
                {' '}
                {t`PowerSchool Global Privacy Statement.`}
              </Link>
              <Link ssr={true} color={PG} href="/tou">
                {' '}
                {t`Find out more here`}
              </Link>
            </WrapText>
          </CheckboxWrap>
          <Controls>
            <Submit
              text={t`Join this site`}
              isLoading={isJoining}
              isDisabled={isSubmitDisabled}
            />
          </Controls>
        </Form>
      </Card.Content>
      <Card.Detour>
        <Submit
          form="reg-membership"
          text={t`Join this site`}
          isLoading={isJoining}
          isDisabled={isSubmitDisabled}
        />
      </Card.Detour>
    </Card>
  )
}

Membership.propTypes = {
  inDialog: PropTypes.bool,
  properties: PropTypes.object.isRequired,
  renderPanel: PropTypes.func.isRequired
}

export { Membership }
